import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import BlogPost from '../templates/blog-post';
import { graphql } from 'gatsby';
import CodeExample from '../components/CodeExample';
export const _frontmatter = {
  "title": "The Shiny New Grid Tools",
  "date": "2017-09-25",
  "promo": "grids",
  "description": "The powerful new tools that come with CSS Grid to make a web dev giddy.",
  "color": "#0071aa"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = BlogPost;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`Flexbox is amazing, works even in older browsers, and mastering it will `}<a parentName="p" {...{
        "href": "/post/what-mastering-flexbox-will-do-for-you"
      }}>{`do a lot for you`}</a>{`. But there's a new kid on the block. CSS Grid makes possible some layouts that are either impossible to achieve with Flexbox or that would take a ton more work.`}</p>
    <p>{`Besides being completely awesome for `}<a parentName="p" {...{
        "href": "/post/grid-beats-flexbox-full-page-layout/"
      }}>{`full page`}</a>{` and `}<a parentName="p" {...{
        "href": "/post/grid-for-responsive-layout/"
      }}>{`responsive`}</a>{` layouts, CSS Grid comes packed with some incredible new tools that make a web dev feel like a freaking rockstar. Check out these new weapons at your disposal as soon as you start using Grids:`}</p>
    <h2>{`Gaps`}</h2>
    <p>{`Grid gaps are like margins on steroids. You can set gaps between your rows and columns, and the gaps are wicked smart: they only apply to the inner tracks! Our outer tracks stay flush against the edges like we want them to 99% of the time.`}</p>
    <CodeExample theme="grid" live={true} showCSS={`
    .container {
      display: grid;
      height: 300px;
      grid-gap: 30px;
    }
  `} fixedCSS={``} html={`
    <div class="container">
      <div class="item orange"></div>
      <div class="item blue"></div>
      <div class="item purple"></div>
    </div>
  `} mdxType="CodeExample" />
    <p>{`We used to have to get this by disabling margins on the first or last child element. But then we also had to worry about what happened when the content shifted to multiple lines making the last child no longer the one that needed its margin removed. It was a pain train, and `}<inlineCode parentName="p">{`gaps`}</inlineCode>{` now handle it for us automatically. It's almost as if someone who built this stuff actually uses CSS themselves!`}</p>
    <p>{`The CSS spec people have recently realized that `}<inlineCode parentName="p">{`gaps`}</inlineCode>{` would be incredibly useful even in flexbox layouts. So they've made the `}<inlineCode parentName="p">{`grid-`}</inlineCode>{` prefix optional and are currently working to enable `}<inlineCode parentName="p">{`gaps`}</inlineCode>{` in flexbox. But for now it's only available when using Grid.`}</p>
    <h2>{`Named Grid Lines`}</h2>
    <p>{`Grid gives us a brand new way to size and position everything that goes into the grid. You can make items span forwards or backwards, overlap with other items, span multiple columns or rows, etc. It really is a much better layout system than anything we've had in the past. Check out `}<a parentName="p" {...{
        "href": "/post/naming-css-grid-lines/"
      }}>{`Naming your CSS Grid Lines`}</a>{` for all the juicy details.`}</p>
    <h2>{`Fraction Units`}</h2>
    <p>{`Grids come with a brand new `}<inlineCode parentName="p">{`fr`}</inlineCode>{` unit that can only be used in a grid. They're very different from percentages and viewport units (`}<inlineCode parentName="p">{`vw`}</inlineCode>{`, `}<inlineCode parentName="p">{`vh`}</inlineCode>{`) etc. When you assign a column or row a fraction unit, it simply means take up a certain fraction of the `}<strong parentName="p">{`free space`}</strong>{` left over after all of the other tracks and gaps take their fixed space.`}</p>
    <p>{`Check it out. In this example we're telling the first (orange) column to be `}<inlineCode parentName="p">{`50px`}</inlineCode>{` wide, our grid to have `}<inlineCode parentName="p">{`10px`}</inlineCode>{` gaps between tracks, letting the second (blue) column take up `}<strong parentName="p">{`1 fraction`}</strong>{` of the remaining space, and the last (purple) column take up `}<strong parentName="p">{`2 fractions`}</strong>{` of the remaining space.`}</p>
    <CodeExample theme="grid" live={true} showCSS={`
    .container {
      display: grid;
      grid-gap: 10px;
      grid-template-columns: 50px 1fr 2fr;
    }
  `} fixedCSS={`
  .container {
      height: 300px;
    }
  `} html={`
    <div class="container">
      <div class="item orange"></div>
      <div class="item blue"></div>
      <div class="item purple"></div>
    </div>
  `} mdxType="CodeExample" />
    <p>{`If you're curious, the math looks something like this:`}</p>
    <div className="formula">
  gridWidth = 300px
  <br />
  totalGaps = gridGap (10px) * 2 = 20px
  <br />
  column1Width = 50px
  <br />
  freeSpace = gridWidth - totalGaps - column1Width = 230px
  <br />
  numFRUnits = 3 (the sum of all the fr units used in the grid columns)
  <br />
  column2Width = freeSpace * ( 1(fr) / numFRUnits) = 230 * (1 / 3) = 76.66px
  <br />
  column3Width = freeSpace * ( 2(fr) / numFRUnits) = 230 * (2 / 3) = 153.33px
  <br />
    </div>
    <p>{`These `}<inlineCode parentName="p">{`fr`}</inlineCode>{` units are legit. They place nicely with all the other units. And unlike percentages which are based on the size of the Grid `}<em parentName="p">{`container`}</em>{`, fractions are based only on the `}<em parentName="p">{`free space`}</em>{` that's left. So you won't accidentally cause overflow/scroll issues like you might if you used percentages.`}</p>
    <p className="note">
  NOTE: It's easy to think fr units are just like flexbox's{' '}
  <strong>flex-grow</strong>, but there's a <em>big</em> difference. We'll cover
  that in a future post.
    </p>
    <h2>{`Minmax`}</h2>
    <p>{`One of my favorite new things that comes with Grid is the `}<inlineCode parentName="p">{`minmax`}</inlineCode>{` value. It lets you set `}<strong parentName="p">{`ranges`}</strong>{` of values for your Grid columns & rows. See `}<a parentName="p" {...{
        "href": "/post/grid-for-responsive-layout/"
      }}>{`grid is perfect for responsive layout`}</a>{` for an example of `}<inlineCode parentName="p">{`minmax`}</inlineCode>{`. This is such a smart unit, I'm really hoping to see it come to more parts of CSS in the future. Imagine being able to set a range for your padding: `}<em parentName="p">{`at least 10px but no more than 10%`}</em>{`. We can kinda do this on a few properties today using `}<inlineCode parentName="p">{`min-width`}</inlineCode>{` and `}<inlineCode parentName="p">{`max-width`}</inlineCode>{` but not on things like `}<inlineCode parentName="p">{`border`}</inlineCode>{` / `}<inlineCode parentName="p">{`margin`}</inlineCode>{` / `}<inlineCode parentName="p">{`padding`}</inlineCode>{` / `}<inlineCode parentName="p">{`flex-grow`}</inlineCode>{` / `}<inlineCode parentName="p">{`box-shadow`}</inlineCode>{` etc. and it's just not as flexible as `}<inlineCode parentName="p">{`minmax`}</inlineCode>{`.`}</p>
    <h2>{`Implicit Tracks`}</h2>
    <p>{`Something way cool about Grids is that you don't need to explicitly spell out a new column or row for each item that will go into your grid. You just define the `}<strong parentName="p">{`template`}</strong>{` using `}<inlineCode parentName="p">{`grid-template-rows`}</inlineCode>{` and `}<inlineCode parentName="p">{`grid-template-columns`}</inlineCode>{`. As soon as your grid has more stuff in it than what you defined, the smart little devil will go ahead and create `}<em parentName="p">{`new`}</em>{` columns & rows beyond what you specified!`}</p>
    <p>{`Watch this: we only define two columns and one row (two cells total). But as soon as a third item went into the grid it created a new second row `}<em parentName="p">{`automatically`}</em>{`:`}</p>
    <CodeExample theme="grid" live={true} showCSS={`
    .container {
      display: grid;
      grid-gap: 10px;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr;
    }
  `} fixedCSS={`
  .container {
      height: 300px;
    }
  `} html={`
    <div class="container">
      <div class="item orange"></div>
      <div class="item blue"></div>
      <div class="item purple"></div>
    </div>
  `} mdxType="CodeExample" />
    <p>{`The reason our purple item is so small is because these `}<strong parentName="p">{`implicit`}</strong>{` (auto-generated) tracks default to a size of `}<inlineCode parentName="p">{`auto`}</inlineCode>{`. Since the purple item doesn't have any content, its only size are its borders. But the smart Grid spec people totally threw us a bone here: we can actually specify exactly how we want any auto-generated implicit tracks to behave!`}</p>
    <p>{`Here's the exact same example but this time telling our Grid to make any new rows take up an equal share of the available space, using `}<inlineCode parentName="p">{`grid-auto-rows: 1fr;`}</inlineCode></p>
    <CodeExample theme="grid" live={true} showCSS={`
    .container {
      display: grid;
      grid-gap: 10px;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr;
      grid-auto-rows: 1fr;
    }
  `} fixedCSS={`
  .container {
      height: 300px;
    }
  `} html={`
    <div class="container">
      <div class="item orange"></div>
      <div class="item blue"></div>
      <div class="item purple"></div>
    </div>
  `} mdxType="CodeExample" />
    <p>{`Sweet right? Our new implicit row forced our original first row to stop hogging all the space. What do you think would happen if we had set `}<inlineCode parentName="p">{`grid-auto-rows`}</inlineCode>{` to a fixed unit, like `}<inlineCode parentName="p">{`50px`}</inlineCode>{`? Play with this example (it's live on the site or you can go to `}<a parentName="p" {...{
        "href": "https://codepen.io/geddski/pen/qPRXYo"
      }}>{`codepen`}</a>{`) and see what happens.`}</p>
    <p>{`I love the granular control Grid layout gives us. I used a lot of implicit rows like this `}<a parentName="p" {...{
        "href": "/post/new-grid-site/"
      }}>{`when I built my new grid site`}</a>{`.`}</p>
    <h2>{`True Column Flow`}</h2>
    <p>{`I once bombed a job interview question many years ago where I was asked to create a "column layout": to make items fill up entire columns first rather than rows first. But the DOM doesn't work this way: it always places elements horizontally:`}</p>
    <CodeExample theme="grid" live={true} showCSS={`
  .container {
    width: 300px;
    height: 300px;
  }\n
  .item { 
    width: 50%;
    float: left;
    height: calc(300px / 3);
  }
  `} html={`
  <div class="container">
    <div class="item numbered orange">1</div>
    <div class="item numbered blue">2</div>
    <div class="item numbered purple">3</div>
    <div class="item numbered red">4</div>
    <div class="item numbered green">5</div>
    <div class="item numbered grey">6</div>
  </div>
  `} mdxType="CodeExample" />
    <p>{`Turning this into a true column layout used to take some JavaScript chops that I clearly didn't have at the time. But with CSS Grid it's now super easy using the `}<inlineCode parentName="p">{`grid-auto-flow`}</inlineCode>{` property:`}</p>
    <CodeExample theme="grid" live={true} showCSS={`
   .container {
      display: grid;
      grid-auto-flow: column;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr 1fr;
      width: 300px;
      height: 300px;
    }
  `} html={`
  <div class="container">
    <div class="item numbered orange">1</div>
    <div class="item numbered blue">2</div>
    <div class="item numbered purple">3</div>
    <div class="item numbered red">4</div>
    <div class="item numbered green">5</div>
    <div class="item numbered grey">6</div>
  </div>
  `} mdxType="CodeExample" />
    <p>{`This made it fill up the left column first rather than the row first, and any new items that get added will make the grid create new implicit `}<em parentName="p">{`columns`}</em>{` instead of implicit `}<em parentName="p">{`rows`}</em>{`. See! It wasn't that I was a complete n00b of a dev, I must have just known in my heart that someday CSS would get awesome enough to do this kind of thing for me ;)`}</p>
    <h2>{`And More`}</h2>
    <p>{`Grid layout brings `}<em parentName="p">{`so`}</em>{` many great new tools to our kit! In a future post I'll cover how awesome `}<inlineCode parentName="p">{`grid areas`}</inlineCode>{` are, `}<inlineCode parentName="p">{`justify-self`}</inlineCode>{` (you know, that thing you tried a million times with flexbox but it simply doesn't have it), and other amazing goodies. Students of `}<a parentName="p" {...{
        "href": "https://gridcritters.com"
      }}>{`Grid Critters`}</a>{` are mastering all of it in the most fun way possible, you should `}<a parentName="p" {...{
        "href": "https://gridcritters.com"
      }}>{`join us`}</a>{`.`}</p>
    <p>{`Have fun playing with all these shiny new Grid tools!`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      